import type { ScreenMethods, ScreenLayout } from "@observe-tech/utils";
import type { SyncStore as Store } from "../../infra/state";
import { defaultScreenLayoutValues } from "./constants";

export const createScreenOperations = (store: Store) => {
  const updateScreenType = (screenId: number, screenType: string) => {
    const currentLayouts = store.get("screenLayouts") || [];

    const updatedLayouts = currentLayouts.map((layout) =>
      layout.id === screenId
        ? { ...layout, screenType: screenType || layout.screenType }
        : layout,
    );

    store.set("screenLayouts", updatedLayouts as ScreenLayout[]);
  };
  // Called on a deliberate sign-out. screenType is persisted per screen with no
  // record of who was signed in, so without this the page one user was last on
  // is reopened for whoever launches the app next.
  const resetScreenTypes = () => {
    const currentLayouts = store.get("screenLayouts") || [];

    store.set(
      "screenLayouts",
      currentLayouts.map((layout) => ({
        ...layout,
        screenType: defaultScreenLayoutValues.screenType,
      })) as ScreenLayout[],
    );
  };

  const setScreens: ScreenMethods["setScreens"] = (screens) => {
    const currentLayouts = store.get("screenLayouts") || [];
    const updatedLayouts = screens.map((screen) => {
      const existingLayout = currentLayouts.find(
        (layout) => layout.id === screen.id,
      );

      const screenLayoutData = existingLayout
        ? existingLayout
        : ({
            ...structuredClone(defaultScreenLayoutValues),
            id: screen.id,
          } as ScreenLayout);

      return {
        ...screenLayoutData,
        id: screen.id,
        isShowing:
          "isShowing" in screen
            ? !!screen.isShowing
            : screenLayoutData.isShowing,
        screenType:
          "screenType" in screen && screen.screenType
            ? screen.screenType
            : screenLayoutData.screenType,
      };
    });

    // Remove layouts that are not in the new screens list
    const filteredLayouts = updatedLayouts.filter((layout) =>
      screens.some((screen) => screen.id === layout.id),
    );

    store.set("screenLayouts", filteredLayouts);
  };

  const getScreens: ScreenMethods["getScreens"] = async () => {
    const currentLayouts = store.get("screenLayouts");
    return (currentLayouts || []).map((layout) => ({
      id: layout.id,
      isShowing: layout.isShowing,
      screenType: layout.screenType,
    }));
  };

  const getScreen: ScreenMethods["getScreen"] = async (screenId) => {
    const currentLayouts = store.get("screenLayouts") || [];
    const layout = currentLayouts.find((l) => l.id === screenId);
    if (!layout) {
      throw new Error(`Screen layout with id ${screenId} not found`);
    }
    return {
      id: layout.id,
      isShowing: layout.isShowing,
      screenType: layout.screenType,
    };
  };

  const resetScreenConfiguration: ScreenMethods["resetScreenConfiguration"] =
    async () => {
      const currentScreens = store.get("screenLayouts") || [];
      const resetScreens = currentScreens.map((screen) => ({
        ...screen,
        isShowing: true,
      }));

      store.set("screenLayouts", resetScreens);
    };

  const saveScreenConfiguration: ScreenMethods["saveScreenConfiguration"] =
    async () => {
      const currentScreens = store.get("screenLayouts") || [];

      // Save the current state as-is
      store.set("screenLayouts", currentScreens);
    };

  return {
    setScreens,
    getScreens,
    getScreen,
    resetScreenConfiguration,
    saveScreenConfiguration,
    updateScreenType,
    resetScreenTypes,
  };
};
